import { useEffect, useState } from 'react';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import type { Rating } from '@/components/shared/QualityFeedback';
import type { GradeBand } from '../constants';

interface UseAiReadingLevelFeedbackResult {
  isSubmitting: boolean;
  rating: Rating;
  submitFeedback: (newRating: 'positive' | 'negative') => void;
}

const useAiReadingLevelFeedback = (
  sectionId: number,
  gradeBand: GradeBand,
  locale: string,
  llmCompletionId: number | null,
): UseAiReadingLevelFeedbackResult => {
  const [rating, setRating] = useState<Rating>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (llmCompletionId === null) return undefined;

    let cancelled = false;

    axios
      .get(Routes.plato_api_ai_reading_level_feedback_section_path(sectionId), {
        params: { grade_band: gradeBand, llm_completion_id: llmCompletionId, locale },
      })
      .then(({ data }) => {
        if (!cancelled) setRating(data.rating);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [sectionId, gradeBand, locale, llmCompletionId]);

  const submitFeedback = (newRating: 'positive' | 'negative') => {
    const previousRating = rating;
    setRating(newRating);
    setIsSubmitting(true);

    axios
      .post(Routes.plato_api_ai_reading_level_feedback_section_path(sectionId), {
        grade_band: gradeBand,
        llm_completion_id: llmCompletionId,
        locale,
        rating: newRating,
      })
      .then(({ data }) => {
        setRating(data.rating);
      })
      .catch(() => {
        setRating(previousRating);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return { isSubmitting, rating, submitFeedback };
};

export default useAiReadingLevelFeedback;
